// Conditional visibility: configured fields drawn only when the broker's answer
// to ANOTHER field says they apply.
//
// TWO SHAPES, ONE QUESTION. A SECTION (BRK-S172) is a named block revealed by a
// checkbox — the parent entity. DEPENDANTS (BRK-S168) are fields that simply
// stop being asked when their unit is answered "not applicable" — a maintenance
// period's length and percent, a liquidated-damages interval's length and
// amount. They share `visibleBondFields`, and they have to: "what can the
// broker actually see" is one question, and two answers to it is how a field
// ends up invisible to the broker and still required by the gate.
//
// ── THE SHAPE ───────────────────────────────────────────────────────────────
//
// The business step gains a checkbox — "Is this business controlled by another
// business?" (`business.controlledByAnotherEntity`) — and, behind it, a second
// set of the same business questions about the parent
// (`parentEntity.legalName`, `parentEntity.ein`, …). Most insureds have no
// parent, so drawing ten more boxes for all of them, permanently, is how a step
// stops being read.
//
// So a rule declares: this CONTROLLER field governs these DEPENDANT fields,
// which render as their own named section, shown when the controller is ticked
// and cleared when it is not.
//
// ⚠️ **CONTRACT GAP, FILED (both tables).** The right home for "these fields
// belong to that one" is the configuration that declares them — a `dependsOn` hint,
// closed-mirrored like every other field attribute. It is not in the contract
// for wave 1, so these tables stand in, and they are the ONLY place in this
// client that names any of these fields. When the hint lands, delete them and
// read the configuration; nothing else changes, because nothing else knows the
// names. Same standing as the bond-penalty pairing (`bondFieldPairs.ts`), and
// the same rule: keep them short. See docs/bonds.md § The parent entity section
// and § Maintenance and liquidated damages.
//
// ── WHAT IS DELIBERATELY NOT HERE ───────────────────────────────────────────
//
// Nothing about `local: true`. A configuration marks a field local when its
// value is PMS's rather than the vendor's, which is a statement about where the
// value goes upstream — not about how it is drawn. A local field renders with
// its declared control like any other, and the moment this client branched on
// that flag it would be deciding a question that is discovery's.
import type {
  BondFieldValue,
  BondRenderField,
  BondValueBag,
} from "@/lib/bondFields";

export interface BondFieldSectionRule {
  /** Stable id — the section's React key and its test handle. */
  id: string;
  /** The field whose value decides whether the section is drawn. */
  controller: string;
  /**
   * Dependants, by path prefix. A prefix rather than a list because the parent
   * entity's questions are whatever the configuration decides to ask about a
   * parent — a list here would silently drop a field the service added.
   */
  prefix: string;
  /** The section's heading. */
  title: string;
  /** Under the heading, when the configuration supplies none of its own. */
  help?: string;
  /**
   * Helper text for the CONTROLLER's own checkbox, used only when the
   * configuration gives that field no `help`. The configuration owns its
   * wording; this stands in for its silence (the `bondOptionLabels` rule).
   */
  controllerHelp?: string;
}

export const BOND_FIELD_SECTIONS: readonly BondFieldSectionRule[] = [
  {
    id: "parentEntity",
    controller: "business.controlledByAnotherEntity",
    prefix: "parentEntity.",
    title: "Parent entity",
    help: "Tell us about the business that controls this one.",
    controllerHelp:
      "This includes a parent company, holding company or other related entity.",
  },
];

/**
 * The answer that means "this does not apply to my project" (BRK-S168).
 *
 * ONE spelling, shared by both units, because it is the CONFIGURATION's own:
 * discovery adds `not_applicable` to `maintenancePeriodUnit` and to
 * `liquidatedDamagesInterval` as a member of each select's option list. It is
 * matched as a VALUE and never inferred from an option's label — a
 * configuration that rewords the label still means the same thing.
 */
export const NOT_APPLICABLE = "not_applicable";

/**
 * A field whose answer switches OFF the fields that only make sense beside it.
 *
 * Deliberately NOT a section: there is no heading and no block, because these
 * are not a separate subject — "how long is the maintenance period" is part of
 * the same question as "is there one". They simply stop being asked, in place.
 *
 * Dependants by explicit NAME rather than by prefix, because they do not share
 * one: `maintenancePeriodLength` and `maintenancePercent` both belong to
 * `maintenancePeriodUnit`, which shares a prefix with the first and would
 * otherwise hide itself.
 */
export interface BondFieldDependantRule {
  id: string;
  /** The field whose value decides. */
  controller: string;
  /** The dependants are hidden while the controller holds one of these. */
  hiddenWhen: readonly string[];
  /** The fields that stop being asked. */
  dependents: readonly string[];
}

export const BOND_FIELD_DEPENDANTS: readonly BondFieldDependantRule[] = [
  {
    id: "maintenancePeriod",
    controller: "maintenancePeriodUnit",
    hiddenWhen: [NOT_APPLICABLE],
    dependents: ["maintenancePeriodLength", "maintenancePercent"],
  },
  {
    id: "liquidatedDamages",
    controller: "liquidatedDamagesInterval",
    hiddenWhen: [NOT_APPLICABLE],
    dependents: ["liquidatedDamagesLength", "liquidatedDamagesAmount"],
  },
];

/**
 * The dependant rule a field is the controller of, or null.
 *
 * Its dependants are cleared the moment the broker answers "not applicable" —
 * the same rule the section's untick follows, and the bond-penalty toggle
 * before it: what the broker sees is what is stored, and a value that vanished
 * silently at submit is the version nobody could debug. Re-answering with a
 * real unit then offers empty boxes rather than half-remembered ones about a
 * maintenance period the broker has just said does not exist.
 */
export function dependantRuleFor(
  field: BondRenderField,
): BondFieldDependantRule | null {
  return (
    BOND_FIELD_DEPENDANTS.find((rule) => rule.controller === field.name) ?? null
  );
}

/** Whether a dependant rule is currently hiding its fields. */
function isHidden(rule: BondFieldDependantRule, values: BondValueBag): boolean {
  const value = values[rule.controller];
  return typeof value === "string" && rule.hiddenWhen.includes(value);
}

/**
 * The dependants to clear when a controller is answered with a hiding value.
 *
 * Only what the STEP declared, so a rule naming a field this configuration does
 * not ask for clears nothing.
 */
export function clearedByBondDependants(
  rule: BondFieldDependantRule,
  fields: readonly BondRenderField[],
  value: BondFieldValue | undefined,
): string[] {
  if (typeof value !== "string" || !rule.hiddenWhen.includes(value)) return [];
  const declared = new Set(fields.map((field) => field.name));
  return rule.dependents.filter((name) => declared.has(name));
}

/** The rule a field is the controller of, or null. */
export function sectionRuleFor(
  field: BondRenderField,
): BondFieldSectionRule | null {
  return (
    BOND_FIELD_SECTIONS.find((rule) => rule.controller === field.name) ?? null
  );
}

/** Whether a rule's section is showing, given what is captured. */
function isOpen(rule: BondFieldSectionRule, values: BondValueBag): boolean {
  return values[rule.controller] === true;
}

/**
 * The step's fields split into the ones that always render and the sections
 * that render conditionally.
 *
 * A rule with **no dependants in this step** produces no section at all — a
 * heading over nothing reads as data that failed to load, and a configuration
 * that declares the checkbox without the parent fields has simply not been
 * updated yet. The controller itself stays an ordinary field in the main grid:
 * it is the question, not part of the answer.
 *
 * Order is otherwise untouched, and sections come last. The configuration
 * decides where its questions sit; a conditional block that appeared in the
 * middle would push everything below it down each time it opened.
 */
export interface BondFieldSectionGroup {
  rule: BondFieldSectionRule;
  fields: BondRenderField[];
  open: boolean;
}

export function resolveBondFieldSections(
  fields: readonly BondRenderField[],
  values: BondValueBag,
): { fields: BondRenderField[]; sections: BondFieldSectionGroup[] } {
  const sections: BondFieldSectionGroup[] = [];
  const claimed = new Set<string>();
  for (const rule of BOND_FIELD_SECTIONS) {
    const owned = fields.filter((field) => field.name.startsWith(rule.prefix));
    if (owned.length === 0) continue;
    for (const field of owned) claimed.add(field.name);
    sections.push({ rule, fields: owned, open: isOpen(rule, values) });
  }
  return {
    fields: fields.filter((field) => !claimed.has(field.name)),
    sections,
  };
}

/**
 * The fields a step should behave as if it had not asked — every dependant of a
 * rule whose controller is not ticked.
 *
 * Used to narrow the list the required check, the format check and the SAVE all
 * work from, so a hidden field can neither block the step nor reach the wire.
 * That is one filter over one list rather than three separate exemptions: the
 * failure this shape rules out is a field that is invisible to the broker and
 * still required by the gate, which is a step nobody can get past and nobody
 * can see why.
 */
export function visibleBondFields(
  fields: readonly BondRenderField[],
  values: BondValueBag,
): BondRenderField[] {
  const closed = BOND_FIELD_SECTIONS.filter((rule) => !isOpen(rule, values));
  // BRK-S168 — the second shape, answered by the SAME function. A maintenance
  // period answered "not applicable" has no length and no percent, and the step
  // must not go on asking for them in any of the four places that read this
  // list.
  const suppressed = new Set(
    BOND_FIELD_DEPENDANTS.filter((rule) => isHidden(rule, values)).flatMap(
      (rule) => rule.dependents,
    ),
  );
  if (closed.length === 0 && suppressed.size === 0) return [...fields];
  return fields.filter(
    (field) =>
      !suppressed.has(field.name) &&
      !closed.some((rule) => field.name.startsWith(rule.prefix)),
  );
}

/**
 * The dependant names to clear when a controller is switched OFF.
 *
 * Cleared at the moment the broker unticks it, not at save time — the
 * `bondFieldPairs` rule: what the broker sees is what is stored, and a value
 * that vanished silently at submit is the version nobody could debug. It also
 * means re-ticking gives a clean section rather than a half-remembered one
 * about a parent the broker has just said does not exist.
 */
export function clearedByBondSection(
  rule: BondFieldSectionRule,
  fields: readonly BondRenderField[],
): string[] {
  return fields
    .filter((field) => field.name.startsWith(rule.prefix))
    .map((field) => field.name);
}
